import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import InvoiceDocument from "@/components/InvoiceDocument";
import { buildInvoicePdf, invoiceFileName, BANK_DETAILS } from "@/lib/invoicePdf";
import { Button } from "@/components/ui/button";

/** Full-screen customer-facing invoice preview with PDF download + native share. */
export default function InvoiceViewer({ invoice, open, onOpenChange }) {
  const [busy, setBusy] = useState(false);

  // A drawer closing underneath can leave pointer-events disabled on body, which swallows
  // the first tap on PDF/Share. Clear it while the viewer is open.
  useEffect(() => {
    if (open) document.body.style.pointerEvents = "";
  }, [open]);

  if (!open || !invoice) return null;

  const savePdf = (pdf) => {
    const name = invoiceFileName(invoice);
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const download = async () => {
    setBusy(true);
    try {
      savePdf(await buildInvoicePdf(invoice));
      toast.success("Invoice PDF saved to your downloads");
    } catch (e) {
      toast.error(`Could not build the PDF: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const pdf = await buildInvoicePdf(invoice);
      const file = new File([pdf.output("blob")], invoiceFileName(invoice), { type: "application/pdf" });
      const canShareFile = typeof navigator !== "undefined" && navigator.canShare?.({ files: [file] });
      if (canShareFile) {
        try {
          await navigator.share({
            files: [file],
            title: `Heels & Tea Invoice ${invoice.invoice_number || ""}`.trim(),
          });
          setBusy(false);
          return;
        } catch (err) {
          if (err?.name === "AbortError") {
            setBusy(false);
            return;
          }
        }
      }
      savePdf(pdf);
      toast.success("PDF saved — open WhatsApp, tap the attach (paperclip) icon, choose Document, and pick this invoice", {
        duration: 9000,
      });
    } catch (e) {
      toast.error(`Could not share the invoice: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const copyAccount = async () => {
    const digits = BANK_DETAILS.account_number;
    try {
      await navigator.clipboard.writeText(digits);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = digits;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success("Copied!", { duration: 1500 });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-neutral-100" data-testid="invoice-viewer">
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2">
        <Button variant="ghost" size="sm" data-testid="invoice-viewer-close" className="h-10" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" /> Close
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" data-testid="invoice-download-pdf" className="h-10" disabled={busy} onClick={download}>
            <Download className="h-4 w-4" /> PDF
          </Button>
          <Button size="sm" data-testid="invoice-share" className="h-10" disabled={busy} onClick={share}>
            <Share2 className="h-4 w-4" /> Share
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto max-w-[820px] bg-white shadow-sm">
          <InvoiceDocument invoice={invoice} onCopyAccount={copyAccount} />
        </div>
      </div>
    </div>
  );
}
